import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Plus,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminActivateLabourTemplate,
  adminListLabourTemplates,
} from "@/lib/api/labour-analyzer.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/labour-templates")({
  head: () => ({
    meta: [
      { title: "Admin · Labour-law guidance templates" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminTemplatesList,
});

const CATEGORIES = [
  "all",
  "system_prompt",
  "ccma",
  "lra",
  "bcea",
  "eea",
  "schedule_8",
  "case_law",
  "drafting",
  "general",
];

function AdminTemplatesList() {
  const qc = useQueryClient();
  const fetchAll = useServerFn(adminListLabourTemplates);
  const activate = useServerFn(adminActivateLabourTemplate);

  const q = useQuery({
    queryKey: ["admin-labour-templates"],
    queryFn: () => fetchAll(),
  });
  const [filter, setFilter] = useState<string>("all");

  const rows = (q.data?.rows ?? []).filter((r: any) =>
    filter === "all" ? true : r.category === filter,
  );

  // Group by slug, latest version first
  const grouped: Record<string, any[]> = {};
  for (const r of rows) {
    (grouped[r.slug] ??= []).push(r);
  }
  const slugs = Object.keys(grouped).sort();

  async function onActivate(id: string) {
    try {
      await activate({ data: { id } });
      toast.success("Activated");
      qc.invalidateQueries({ queryKey: ["admin-labour-templates"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not activate");
    }
  }

  return (
    <div className="space-y-6">
      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <BookOpen className="h-4 w-4" /> Admin · Guidance library
        </div>
        <h1 className="font-display text-2xl text-navy">
          Labour-law & CCMA guidance templates
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Curate the legal knowledge the AI Labour-Law Analyzer applies. Each template
          is versioned — publish a new version any time you want to refine the rules,
          add new case law, or update CCMA procedure. Only the version marked{" "}
          <strong className="text-emerald-700">active</strong> is fed into the AI.
        </p>
        <div className="mt-4">
          <Link
            to={"/portal/admin/labour-templates/new" as any}
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-2 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
          >
            <Plus className="h-3.5 w-3.5" /> New template
          </Link>
        </div>
      </header>

      <div className="flex flex-wrap gap-1">
        {CATEGORIES.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`rounded-full border px-3 py-1 text-xs ${
              filter === c
                ? "border-navy bg-navy text-navy-foreground"
                : "border-input bg-background"
            }`}
          >
            {c.replaceAll("_", " ")}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : slugs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          No templates yet.
        </p>
      ) : (
        <ul className="space-y-4">
          {slugs.map((slug) => {
            const versions = grouped[slug];
            const latest = versions[0];
            return (
              <li
                key={slug}
                className="rounded-xl border border-border bg-card p-4 shadow-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                      {latest.category.replaceAll("_", " ")} · slug:{" "}
                      <span className="font-mono">{slug}</span>
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-navy">
                      {latest.title}
                    </p>
                  </div>
                  <Link
                    to={"/portal/admin/labour-templates/$id" as any}
                    params={{ id: latest.id } as any}
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:border-navy"
                  >
                    Open latest <ChevronRight className="h-3 w-3" />
                  </Link>
                </div>
                <ul className="mt-3 space-y-1 text-xs">
                  {versions.map((v: any) => (
                    <li
                      key={v.id}
                      className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <span className="font-mono text-[11px]">v{v.version}</span>{" "}
                        · <span className="text-muted-foreground">{v.title}</span>
                        {v.notes && (
                          <span className="ml-2 line-clamp-1 text-[11px] text-muted-foreground">
                            — {v.notes}
                          </span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {v.is_active ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-900">
                            <CheckCircle2 className="h-3 w-3" /> active
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onActivate(v.id)}
                            className="rounded-md border border-input bg-background px-2 py-0.5 text-[10px] text-navy hover:border-navy"
                          >
                            Make active
                          </button>
                        )}
                        <Link
                          to={"/portal/admin/labour-templates/$id" as any}
                          params={{ id: v.id } as any}
                          className="text-[11px] text-muted-foreground hover:text-navy"
                        >
                          Edit
                        </Link>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Active templates are loaded for every new analysis. To roll back, simply make
        an earlier version active again — no data is lost.
      </p>
    </div>
  );
}
